/* eslint-disable max-len */
/* eslint-disable jsx-a11y/control-has-associated-label */
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInstagram, faTiktok, faLinkedin } from '@fortawesome/free-brands-svg-icons';
import './menu.scss';
import logo from '../../assets/images/logo/ZovLogoWhite.png';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';

// Constants - Updated Design System
const PRIMARY_COLOR = '#0a1628'; // Deep Navy
const ACCENT_COLOR = '#c9a962'; // Elegant Gold
const TEXT_LIGHT = '#ffffff';
const FONT_PRIMARY = "'Inter', sans-serif";

// Styles
const styles = {
  appBar: {
    position: 'fixed',
    width: '100%',
    top: 0,
    left: 0,
    zIndex: 900,
    transition: 'all 0.3s ease-in-out',
  },
  appBarScrolled: {
    backgroundColor: 'rgba(10, 22, 40, 0.98)',
    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.15)',
    backdropFilter: 'blur(12px)',
  },
  appBarTransparent: {
    background: 'linear-gradient(to bottom, rgba(10, 22, 40, 0.8), transparent)',
    boxShadow: 'none',
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 40px',
    alignItems: 'center',
  },
  logoContainer: {
    textDecoration: 'none',
    display: 'block',
  },
  logo: {
    height: '80px',
    transition: 'all 0.3s ease',
  },
  desktopNav: {
    display: 'flex',
    alignItems: 'center',
  },
  navItemContainer: {
    position: 'relative',
    margin: '0 20px',
    '&.active': {
      '& .nav-item': {
        color: ACCENT_COLOR,
      },
    },
  },
  navItem: {
    color: TEXT_LIGHT,
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '2px',
    padding: '10px 0',
    transition: 'all 0.3s ease',
    display: 'block',
    fontFamily: FONT_PRIMARY,
    '&:hover': {
      color: ACCENT_COLOR,
    },
  },
  navItemWithDropdown: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  dropdownIcon: {
    marginLeft: '4px',
    fontSize: '18px',
    color: TEXT_LIGHT,
    transition: 'all 0.3s ease',
  },
  dropdownMenu: {
    position: 'absolute',
    top: 'calc(100% + 15px)',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(10, 22, 40, 0.98)',
    minWidth: '240px',
    padding: '8px 0',
    zIndex: 1000,
    boxShadow: '0 15px 40px rgba(0, 0, 0, 0.25)',
    backdropFilter: 'blur(16px)',
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  dropdownMenuArrow: {
    position: 'absolute',
    top: '-6px',
    left: '50%',
    marginLeft: '-6px',
    width: '12px',
    height: '12px',
    backgroundColor: 'rgba(10, 22, 40, 0.98)',
    transform: 'rotate(45deg)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderBottom: 'none',
    borderRight: 'none',
  },
  dropdownContent: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '400px',
    overflowY: 'auto',
  },
  dropdownItem: {
    color: 'rgba(255, 255, 255, 0.8)',
    textDecoration: 'none',
    padding: '10px 20px',
    fontSize: '13px',
    fontWeight: '400',
    letterSpacing: '0.5px',
    transition: 'all 0.2s ease',
    display: 'block',
    fontFamily: FONT_PRIMARY,
    borderLeft: '2px solid transparent',
  },
  dropdownItemHover: {
    backgroundColor: 'rgba(201, 169, 98, 0.1)',
    color: ACCENT_COLOR,
    borderLeftColor: ACCENT_COLOR,
  },
  rightActions: {
    display: 'flex',
    alignItems: 'center',
  },
  menuToggle: {
    color: TEXT_LIGHT,
    padding: '8px',
    backgroundColor: 'rgba(212, 175, 55, 0.15)',
    borderRadius: '4px',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(212, 175, 55, 0.25)',
    },
  },
  menuIcon: {
    fontSize: '28px',
  },
  ctaButton: {
    backgroundColor: 'transparent',
    color: ACCENT_COLOR,
    border: `1px solid ${ACCENT_COLOR}`,
    padding: '12px 28px',
    fontSize: '13px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '2px',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: '30px',
    fontFamily: FONT_PRIMARY,
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
  },
  ctaButtonHover: {
    backgroundColor: ACCENT_COLOR,
    color: PRIMARY_COLOR,
  },
  mobileMenuContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: '0',
    backgroundColor: PRIMARY_COLOR,
  },
  mobileMenuHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  },
  mobileLogo: {
    height: '45px',
  },
  closeButton: {
    color: ACCENT_COLOR,
    padding: '8px',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(212, 175, 55, 0.1)',
      borderRadius: '50%',
    },
  },
  mobileMenuItems: {
    display: 'flex',
    flexDirection: 'column',
    padding: '16px 24px',
    flexGrow: 1,
    overflowY: 'auto',
  },
  mobileMenuItemWrapper: {
    margin: '5px 0',
  },
  mobileMenuItem: {
    color: TEXT_LIGHT,
    textDecoration: 'none',
    padding: '16px 0',
    fontSize: '16px',
    fontWeight: '500',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    transition: 'all 0.3s ease',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    fontFamily: FONT_PRIMARY,
    '&:hover': {
      color: ACCENT_COLOR,
    },
  },
  mobileMenuItemActive: {
    color: ACCENT_COLOR,
    fontWeight: '600',
  },
  mobileSubmenu: {
    overflow: 'hidden',
    marginTop: '5px',
    marginBottom: '10px',
  },
  mobileSubmenuItem: {
    color: 'rgba(255, 255, 255, 0.8)',
    textDecoration: 'none',
    padding: '14px 16px',
    fontSize: '14px',
    display: 'block',
    transition: 'all 0.3s ease',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    fontFamily: FONT_PRIMARY,
    '&:hover': {
      color: ACCENT_COLOR,
    },
  },
  mobileSubmenuItemActive: {
    color: ACCENT_COLOR,
    backgroundColor: 'rgba(212, 175, 55, 0.1)',
    borderLeft: `2px solid ${ACCENT_COLOR}`,
    paddingLeft: '14px',
  },
  mobileMenuFooter: {
    padding: '24px',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    backgroundColor: 'rgba(0, 0, 0, 0.15)',
  },
  mobileSocialIcons: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  mobileSocialIcon: {
    color: ACCENT_COLOR,
    fontSize: '24px',
    margin: '0 12px',
    padding: '8px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    width: '44px',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      transform: 'translateY(-3px)',
    },
  },
  mobileContactInfo: {
    textAlign: 'center',
  },
  mobileContactPhone: {
    color: TEXT_LIGHT,
    textDecoration: 'none',
    display: 'block',
    marginBottom: '12px',
    fontSize: '16px',
    fontWeight: '500',
    fontFamily: FONT_PRIMARY,
    padding: '12px 24px',
    backgroundColor: 'rgba(212, 175, 55, 0.15)',
    borderRadius: '4px',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(212, 175, 55, 0.25)',
    },
  },
  mobileContactAddress: {
    color: 'rgba(255, 255, 255, 0.7)',
    margin: '16px 0 0 0',
    fontSize: '14px',
    fontFamily: FONT_PRIMARY,
    lineHeight: '1.6',
  },
  headerSpacer: {
    height: '0px',
  },
  // Mobile styles
  mobileStyles: {
    toolbar: {
      padding: '10px 20px',
    },
    logo: {
      height: '65px',
    },
    desktopNav: {
      display: 'none',
    },
  }
};

function MenuComponent() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [productsMenuOpen, setProductsMenuOpen] = useState(false);
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [ctaHovered, setCtaHovered] = useState(false);

  // Check if device is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 900);
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);

    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Detect scroll for navbar background
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  // Close menus when route changes
  useEffect(() => {
    setDrawerOpen(false);
    setProductsMenuOpen(false);
    setMobileSubmenuOpen(false);
  }, [location]);

  // Handle smooth scrolling for anchor links
  const handleLinkClick = (e, path) => {
    if (path.startsWith('/#')) {
      e.preventDefault();
      const targetId = path.substring(2);
      const targetElement = document.getElementById(targetId);

      if (targetElement) {
        setDrawerOpen(false);
        window.scrollTo({
          top: targetElement.offsetTop - 100, // Adjust for header height
          behavior: 'smooth'
        });
      }
    }
  };

  const toggleDrawer = (open) => (event) => {
    if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const handleProductsMouseEnter = () => {
    setProductsMenuOpen(true);
  };

  const handleProductsMouseLeave = () => {
    setProductsMenuOpen(false);
    setHoveredItem(null);
  };

  const toggleMobileSubmenu = () => {
    setMobileSubmenuOpen(!mobileSubmenuOpen);
  };

  // Animation variants
  const menuVariants = {
    hidden: { opacity: 0, y: 5, scale: 0.98 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1],
        staggerChildren: 0.03,
      },
    },
    exit: {
      opacity: 0,
      y: 5,
      scale: 0.98,
      transition: {
        duration: 0.25,
        ease: [0.16, 1, 0.3, 1],
      }
    },
  };

  const menuItemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  const mobileMenuVariants = {
    closed: { height: 0, opacity: 0 },
    open: {
      height: 'auto',
      opacity: 1,
      transition: {
        height: { duration: 0.4 },
        opacity: { duration: 0.3 },
        staggerChildren: 0.05,
        delayChildren: 0.05,
      },
    },
  };

  const mobileMenuItemVariants = {
    closed: { opacity: 0, x: -20 },
    open: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1],
      }
    }
  };

  const drawerVariants = {
    closed: { x: '100%' },
    open: {
      x: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30,
      }
    },
    exit: {
      x: '100%',
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30,
      }
    }
  };

  // Product menu categories
  const productCategories = [
    { name: t('nav.kitchens'), path: '/kitchens' },
    { name: t('nav.officeCabinet'), path: '/office' },
    { name: t('nav.hotelFurniture'), path: '/hotel-furniture' },
    { name: t('nav.livingRoom'), path: '/livingRooms' },
    { name: t('nav.bedroom'), path: '/bedrooms' },
    { name: t('nav.bathroom'), path: '/bathrooms' },
    { name: t('nav.interiorDoors'), path: '/interior-doors' },
    { name: t('nav.tableChairs'), path: '/table-and-chairs' },
    { name: t('nav.closetSliding'), path: '/closet-and-sliding-systems' },
    { name: t('nav.homeAccessories'), path: '/home-accessories' },
    { name: t('nav.materialsFitting'), path: '/materials-fitting-and-accessories' },
    { name: t('nav.interiorElements'), path: '/interior-elements' },
  ];

  // Main navigation items
  const navItems = [
    { name: t('nav.aboutUs'), path: '/about' },
    { name: t('nav.products'), path: '/concepts', hasSubmenu: true },
    { name: t('nav.portfolio'), path: '/portfolio' },
    { name: t('nav.ourShowroom'), path: '/location' },
    { name: t('nav.contact'), path: '/contact' },
  ];

  // Mobile drawer content
  const renderDrawerContent = () => (
    <motion.div
      style={styles.mobileMenuContainer}
      initial="closed"
      animate="open"
      exit="exit"
      variants={drawerVariants}
    >
      <Box sx={styles.mobileMenuHeader}>
        <Link to="/" onClick={toggleDrawer(false)}>
          <img src={logo} alt="ZOV Furniture LLC" style={styles.mobileLogo} />
        </Link>
        <IconButton
          onClick={toggleDrawer(false)}
          sx={styles.closeButton}
          aria-label="close menu"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={styles.mobileMenuItems}>
        {navItems.map((item) => (
          <motion.div
            key={item.name}
            style={styles.mobileMenuItemWrapper}
            variants={menuItemVariants}
          >
            {item.hasSubmenu ? (
              <>
                <div
                  style={{
                    ...styles.mobileMenuItem,
                    ...(location.pathname.includes(item.path) ? styles.mobileMenuItemActive : {}),
                    cursor: 'pointer'
                  }}
                  onClick={toggleMobileSubmenu}
                >
                  <span>{item.name}</span>
                  <KeyboardArrowDownIcon
                    sx={{
                      transform: mobileSubmenuOpen ? 'rotate(180deg)' : 'rotate(0)',
                      transition: 'transform 0.3s',
                      color: mobileSubmenuOpen ? ACCENT_COLOR : 'inherit',
                    }}
                  />
                </div>
                <AnimatePresence>
                  {mobileSubmenuOpen && (
                    <motion.div
                      style={styles.mobileSubmenu}
                      initial="closed"
                      animate="open"
                      exit="closed"
                      variants={mobileMenuVariants}
                    >
                      {productCategories.map((category) => (
                        <motion.div
                          key={category.path}
                          variants={mobileMenuItemVariants}
                        >
                          <Link
                            to={category.path}
                            style={{
                              ...styles.mobileSubmenuItem,
                              ...(location.pathname === category.path ? styles.mobileSubmenuItemActive : {})
                            }}
                            onClick={(e) => {
                              toggleDrawer(false)(e);
                              handleLinkClick(e, category.path);
                            }}
                          >
                            {category.name}
                          </Link>
                        </motion.div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            ) : (
              <Link
                to={item.path}
                style={{
                  ...styles.mobileMenuItem,
                  ...(location.pathname === item.path ? styles.mobileMenuItemActive : {})
                }}
                onClick={(e) => {
                  toggleDrawer(false)(e);
                  handleLinkClick(e, item.path);
                }}
              >
                {item.name}
              </Link>
            )}
          </motion.div>
        ))}
      </Box>

      <Box sx={styles.mobileMenuFooter}>
        {/* Language Switcher in Mobile Menu */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
          <LanguageSwitcher />
        </div>
        <div style={styles.mobileSocialIcons}>
          <a href="https://www.instagram.com/zovfurnitureuae/?igsh=NGFrdXd1NTdyNm1i" target="_blank" rel="noopener noreferrer" style={styles.mobileSocialIcon}>
            <FontAwesomeIcon icon={faInstagram} />
          </a>
          <a href="https://www.tiktok.com/@zovfurnitureuae" target="_blank" rel="noopener noreferrer" style={styles.mobileSocialIcon}>
            <FontAwesomeIcon icon={faTiktok} />
          </a>
          <a href="https://www.linkedin.com/company/zov-furniture-l-l-c/about/" target="_blank" rel="noopener noreferrer" style={styles.mobileSocialIcon}>
            <FontAwesomeIcon icon={faLinkedin} />
          </a>
        </div>
        <div style={styles.mobileContactInfo}>
          <a href="tel:+971509636346" style={styles.mobileContactPhone}>+971 50 963 6346</a>
          <p style={styles.mobileContactAddress}>Grand Floor NBH Tower<br />Abu Dhabi - UAE</p>
        </div>
      </Box>
    </motion.div>
  );

  return (
    <>
      <header
        style={{
          ...styles.appBar,
          ...(scrolled ? styles.appBarScrolled : styles.appBarTransparent)
        }}
      >
        <div style={isMobile ? { ...styles.toolbar, ...styles.mobileStyles.toolbar } : styles.toolbar}>
          {/* Logo */}
          <Link to="/" style={styles.logoContainer}>
            <img
              src={logo}
              alt="ZOV Furniture LLC"
              style={isMobile ? { ...styles.logo, ...styles.mobileStyles.logo } : styles.logo}
            />
          </Link>

          {/* Desktop Navigation */}
          <div style={isMobile ? styles.mobileStyles.desktopNav : styles.desktopNav}>
            {navItems.map((item) => (
              <div
                key={item.name}
                style={{
                  ...styles.navItemContainer,
                  ...(location.pathname === item.path ? { '& .nav-item': { color: ACCENT_COLOR } } : {})
                }}
                onMouseEnter={item.hasSubmenu ? handleProductsMouseEnter : undefined}
                onMouseLeave={item.hasSubmenu ? handleProductsMouseLeave : undefined}
              >
                {item.hasSubmenu ? (
                  <>
                    <div style={styles.navItemWithDropdown}>
                      <Link
                        to={item.path}
                        style={{
                          ...styles.navItem,
                          color: productsMenuOpen ? ACCENT_COLOR : TEXT_LIGHT
                        }}
                      >
                        {item.name}
                      </Link>
                      <KeyboardArrowDownIcon style={{
                        ...styles.dropdownIcon,
                        color: productsMenuOpen ? ACCENT_COLOR : TEXT_LIGHT,
                        transform: productsMenuOpen ? 'rotate(180deg)' : 'none'
                      }}
                      />
                    </div>
                    <AnimatePresence>
                      {productsMenuOpen && (
                        <motion.div
                          style={styles.dropdownMenu}
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          variants={menuVariants}
                        >
                          <div style={styles.dropdownMenuArrow} />
                          <motion.div style={styles.dropdownContent}>
                            {productCategories.map((category) => (
                              <motion.div
                                key={category.path}
                                variants={menuItemVariants}
                                onMouseEnter={() => setHoveredItem(category.path)}
                                onMouseLeave={() => setHoveredItem(null)}
                              >
                                <Link
                                  to={category.path}
                                  style={{
                                    ...styles.dropdownItem,
                                    ...(hoveredItem === category.path ? styles.dropdownItemHover : {})
                                  }}
                                >
                                  {category.name}
                                </Link>
                              </motion.div>
                            ))}
                          </motion.div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                ) : (
                  <Link
                    to={item.path}
                    style={styles.navItem}
                    onClick={(e) => handleLinkClick(e, item.path)}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}

            {/* CTA Button */}
            <Link
              to="/contact"
              style={{
                ...styles.ctaButton,
                ...(ctaHovered ? styles.ctaButtonHover : {})
              }}
              onMouseEnter={() => setCtaHovered(true)}
              onMouseLeave={() => setCtaHovered(false)}
            >
              {t('nav.getQuote')}
            </Link>

            {/* Language Switcher */}
            <div style={{ marginLeft: '20px' }}>
              <LanguageSwitcher />
            </div>
          </div>

          {/* Right Side - Mobile Menu Toggle */}
          <div style={styles.rightActions}>
            <IconButton
              sx={{
                ...styles.menuToggle,
                display: isMobile ? 'flex' : 'none'
              }}
              aria-label="open menu"
              edge="end"
              onClick={toggleDrawer(true)}
            >
              <MenuIcon sx={styles.menuIcon} />
            </IconButton>
          </div>
        </div>
      </header>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            width: '85%',
            maxWidth: '360px',
            background: 'transparent',
            color: TEXT_LIGHT,
            boxShadow: 'none',
          },
        }}
      >
        {renderDrawerContent()}
      </Drawer>

      {/* Spacer for fixed header */}
      <div style={styles.headerSpacer} />
    </>
  );
}

export default MenuComponent;
